import React, { useState, useRef } from 'react'
import Slider from 'react-slick'
import { useMemo } from 'react'
import PropTypes from 'prop-types'
import Icon from '../icon/Icon'
import Button from '../button/Button'
import Tooltip from '../tooltip/Tooltip'
import type {
  PaginationProps,
  ResultsProps,
  usePaginationProps,
} from './Pagination.types'
import './pagination.scss'

const DOTS = '...'

const range = (start: number, end: number) => {
  let length = end - start + 1
  return Array.from({ length }, (_, idx) => idx + start)
}

const usePagination = ({
  totalCount = 1,
  pageSize = 1,
  siblingCount = 1,
  current = 1,
}: usePaginationProps) => {
  const paginationRange = useMemo(() => {
    const totalPageCount = Math.ceil(totalCount / pageSize)
    const totalPageNumbers = siblingCount + 5

    if (totalPageNumbers >= totalPageCount) {
      return range(1, totalPageCount)
    }

    if (totalPageCount <= 6) {
      let oneRange = range(1, totalPageCount)
      return [...oneRange]
    }

    const leftSiblingIndex = Math.max(current - siblingCount, 1)
    const rightSiblingIndex = Math.min(current + siblingCount, totalPageCount)
    const shouldShowLeftDots = leftSiblingIndex > 2
    const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2
    const firstPageIndex = 1
    const lastPageIndex = totalPageCount

    if (totalPageCount >= 7 && totalPageCount <= 12) {
      const half = totalPageCount / 2 - 1
      const shouldShowLeftDots = leftSiblingIndex > half
      const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2

      if (shouldShowRightDots && !shouldShowLeftDots) {
        let leftItemCount = half + 2 * siblingCount
        let leftRange = range(1, leftItemCount)
        return [...leftRange, DOTS, totalPageCount]
      }

      if (shouldShowLeftDots && !shouldShowRightDots) {
        let rightItemCount = half + 2 * siblingCount
        let rightRange = range(
          totalPageCount - rightItemCount + 1,
          totalPageCount
        )
        return [firstPageIndex, DOTS, ...rightRange]
      }

      if (shouldShowLeftDots && shouldShowRightDots) {
        let rightItemCount = half + 2 * siblingCount
        let rightRange = range(
          totalPageCount - rightItemCount + 1,
          totalPageCount
        )
        return [firstPageIndex, DOTS, ...rightRange]
      }
    }

    if (!shouldShowLeftDots && shouldShowRightDots) {
      let leftItemCount = 3 + 2 * siblingCount
      let leftRange = range(1, leftItemCount)
      return [...leftRange, DOTS, totalPageCount]
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      let rightItemCount = 3 + 2 * siblingCount
      let rightRange = range(
        totalPageCount - rightItemCount + 1,
        totalPageCount
      )
      return [firstPageIndex, DOTS, ...rightRange]
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      let middleRange = range(leftSiblingIndex, rightSiblingIndex)
      return [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex]
    }
  }, [totalCount, pageSize, siblingCount, current])

  return paginationRange
}

const Results = ({
  totalCount = 1,
  pageSize = 1,
  current = 1,
}: ResultsProps) => {
  const totalPage = Math.ceil(totalCount / pageSize)
  const ranges = []
  let currentInit = 1

  for (let i = 0; i < totalPage; i++) {
    let currentLast = currentInit + pageSize - 1
    ranges.push([currentInit, currentLast])
    currentInit = currentLast + 1
  }

  const range = ranges[current - 1].join(' - ')

  return (
    <div
      className='sb-pagination__results'
      data-testid='sb-pagination__results'
    >
      Resultados {range} de {totalCount}
    </div>
  )
}

const Pagination = ({
  text,
  currentPage,
  totalCount,
  pageSize,
  showResults,
  className,
  groupBy,
  groupScroll,
  helperTextLeft,
  helperTextRight,
  onChange,
}: PaginationProps) => {
  const [current, setCurrentPage] = useState<number>(currentPage ?? 1)
  const [isTooltipLeftActive, setIsTooltipLeftActive] = useState(false)
  const [isTooltipRightActive, setIsTooltipRightActive] = useState(false)
  const siblingCount = 1

  const slider = useRef<any>(null)

  const settings = {
    arrows: true,
    dots: false,
    centerPadding: '0',
    infinite: false,
    speed: 500,
    slidesToShow: groupBy,
    slidesToScroll: groupScroll,
    nextArrow: (
      <Button type='clear' color='primary' size='md' iconName='chevron-right' />
    ),
    prevArrow: (
      <Button type='clear' color='primary' size='md' iconName='chevron-left' />
    ),
  }

  const paginationRange = usePagination({
    current,
    totalCount,
    siblingCount,
    pageSize,
  })

  if (current === 0 || (paginationRange && paginationRange.length < 2)) {
    return null
  }

  const pagesGroupBy = []
  for (let i = 1; i < (totalCount ?? 0) / (pageSize ?? 0) + 1; i++) {
    pagesGroupBy.push(i)
  }

  const onPageChange = (page: number) => {
    setCurrentPage(page)
    onChange && onChange(page)
  }
  const onNext = () => onPageChange(current + 1)
  const onPrevious = () => onPageChange(current - 1)

  let lastPage = paginationRange && paginationRange[paginationRange.length - 1]

  return (
    <div
      className='sb-pagination__container'
      data-testid='sb-pagination__container'
    >
      {!!groupBy ? (
        <Slider {...settings} ref={slider}>
          {pagesGroupBy.map((pageNumber, index) => {
            return (
              <div
                onClick={() => onPageChange(pageNumber)}
                className='sb-pagination__item'
                data-testid='sb-pagination__item'
                key={`sb-pagination__item-${index}`}
              >
                <span
                  className={`sb-pagination__item--number ${
                    pageNumber === current && 'sb-pagination__item--selected'
                  }`}
                >
                  {pageNumber}
                </span>
                {text && pageNumber === current && (
                  <span className='text-primary-60 font-bold text-small-02'>
                    {text}
                  </span>
                )}
              </div>
            )
          })}
        </Slider>
      ) : (
        <ul
          className={['sb-pagination', className].join(' ')}
          data-testid='sb-pagination'
        >
          {helperTextLeft ? (
            <Tooltip
              position='top'
              text={helperTextLeft}
              active={isTooltipLeftActive && !(current === 1)}
              cross={false}
              size='md'
              children={
                <li
                  className={`sb-pagination__arrow  sb-pagination__arrow--left ${
                    current === 1 && 'sb-pagination__arrow--disabled'
                  }`}
                  onClick={onPrevious}
                  onMouseOver={() => {
                    setIsTooltipLeftActive(true)
                  }}
                  onMouseLeave={() => {
                    setIsTooltipLeftActive(false)
                  }}
                  key='sb-pagination__arrow--left'
                >
                  <Icon iconName='chevron-left' size={20} />
                </li>
              }
            />
          ) : (
            <li
              key='sb-pagination__arrow--left'
              data-testid='sb-pagination__arrow--left'
              className={`sb-pagination__arrow  sb-pagination__arrow--left ${
                current === 1 && 'sb-pagination__arrow--disabled'
              }`}
              onClick={onPrevious}
            >
              <Icon iconName='chevron-left' size={20} />
            </li>
          )}
          {paginationRange?.map((pageNumber, index) => {
            if (pageNumber === DOTS) {
              return (
                <li className='sb-pagination-dots' key='sb-pagination-dots'>
                  &#8230;
                </li>
              )
            }
            return (
              <li
                onClick={() => onPageChange(Number(pageNumber))}
                className='sb-pagination__item'
                data-testid='sb-pagination__item'
                key={`sb-pagination__item-${index}`}
              >
                <span
                  className={`sb-pagination__item--number ${
                    pageNumber === current && 'sb-pagination__item--selected'
                  }`}
                >
                  {pageNumber}
                </span>
                {text && pageNumber === current && (
                  <span className='text-primary-60 font-bold text-small-02'>
                    {text}
                  </span>
                )}
              </li>
            )
          })}
          {helperTextRight ? (
            <Tooltip
              position='top'
              text={helperTextRight}
              active={isTooltipRightActive && !(current === lastPage)}
              cross={false}
              size='md'
              children={
                <li
                  className={`sb-pagination__arrow sb-pagination__arrow--right ${
                    current === lastPage && 'sb-pagination__arrow--disabled'
                  }`}
                  onClick={onNext}
                  onMouseOver={() => {
                    setIsTooltipRightActive(true)
                  }}
                  onMouseLeave={() => {
                    setIsTooltipRightActive(false)
                  }}
                >
                  <Icon iconName='chevron-right' size={20} />
                </li>
              }
            />
          ) : (
            <li
              data-testid='sb-pagination__arrow--right'
              className={`sb-pagination__arrow sb-pagination__arrow--right ${
                current === lastPage && 'sb-pagination__arrow--disabled'
              }`}
              onClick={onNext}
            >
              <Icon iconName='chevron-right' size={20} />
            </li>
          )}
        </ul>
      )}
      {showResults && (
        <Results
          totalCount={totalCount}
          current={current}
          pageSize={pageSize}
        />
      )}
    </div>
  )
}

Pagination.propTypes = {
  /**
   * Pagination text
   */
  text: PropTypes.string,
  /**
   * Pagination currentPage
   */
  currentPage: PropTypes.number,
  /**
   * Pagination totalCount
   */
  totalCount: PropTypes.number,
  /**
   * Pagination pageSize
   */
  pageSize: PropTypes.number,
  /**
   * showResults?
   */
  showResults: PropTypes.bool,
  /**
   * Pagination className
   */
  className: PropTypes.string,
  /**
   * groupBy?
   */
  groupBy: PropTypes.number,
  /**
   * Pagination helperTextLeft
   */
  helperTextLeft: PropTypes.string,
  /**
   * Pagination helperTextRight
   */
  helperTextRight: PropTypes.string,
  /**
   * Pagination onChange
   */
  onChange: PropTypes.func,
}

Pagination.defaultProps = {
  text: '',
  currentPage: 1,
  totalCount: 100,
  pageSize: 10,
  showResults: false,
  className: '',
  groupBy: 0,
  groupScroll: 5,
  helperTextLeft: '',
  helperTextRight: '',
  onChange: () => {},
}

export default Pagination
